import { ref, reactive, computed, watch, type ComponentPublicInstance, type Ref } from 'vue';
import queryString from 'query-string';
import { debounce } from 'lodash';

import { useCustomer } from '@/use/customer';
import { useRide } from '@/use/ride';
import { useRideTypes } from '@/use/rideTypes';
import { useGlobal } from '@/use/global';
import { callerService } from '@/services/caller';
import { smsTemplateService } from '@/services/smsTemplates';
import type { CallerAdditionalInfo } from '@/models/caller';
import { RideOrderingStep, type RideOrderData, type OrderedRideDetail, type RideFormData } from '@/models/rideOrdering';
import { RideServiceType, type DeliveryInfoFormResult, type RideEstimate } from '@/models/ride';
import rideUtility from '@/Utils/rideUtility';
import { canOfferNashDelivery } from '@/services/operators/viewModel';
import { isDeliveryServiceType } from '@/Utils/deliveryInfoForm';
import { showWarningPopupDialog } from '@/use/swalDialog';
import { usePartner } from '@/use/partner';
import {
  pickupLocation,
  pickupVenueNote,
  dropoffVenueNote,
  formatLocationAddress,
  loadRecentRideLocations,
  resetRideLocations,
} from './rideLocationsViewModel';

// Composables
const {
  customerInfo,
  customerPartnerId,
  customerPartner,
  isScheduledRideRestricted,
  contactPhoneNumbers,
  setCustomerInfo,
  isAllowedToExpandVehicleSearch,
  checkAndShowAdaDialog,
  customerPartnerCode,
} = useCustomer();
const { availableServiceTypeList } = useRide();
const { getScheduledRideTypes, getOrderRideTypes, getAvailableRideType } = useRideTypes();
const { teamLeadsAndSupsList } = useGlobal();
const { partnerCode } = usePartner();

export { checkAndShowAdaDialog };

// State
export const step: Ref<RideOrderingStep> = ref(RideOrderingStep.Location);
export const rideFormType = ref('now');
export const mapReady = ref(false);
export const scheduledRideId = ref(0);

export const orderRideRequest: Ref<RideFormData> = ref({
  phoneNumber: '',
  riderPhone: '',
  serviceType: '',
  rideType: '',
});

export const status = reactive({
  isLoading: false,
  loadingMessage: '',
  isShowAccessibilityDialog: false,
  errorMessage: '',
  isUberSurge: false,
});

export const estimationRide: Ref<RideEstimate | null> = ref(null);
export const estimationList: Ref<RideEstimate[]> = ref([]);
export const defaultEstimateData: Ref<any | null> = ref(null);
export const allPartnerEstimates: Ref<any[]> = ref([]);
export const selectedPartnerEstimateIndex = ref(0);
export const isEstimationOptionsMode = ref(false);
export const isInLyftAssistedTestGroup = ref(false);
export const rideOrderData: Ref<RideOrderData | null> = ref(null);
export const rideDetails: Ref<OrderedRideDetail | null> = ref(null);
export const operatorConfirmedSurgePriceWithCustomer = ref(false);
export const driverNotePickUp = ref('');
export const riderClothes = ref('');
export const driverArrival = ref('');
export const newPreferredContactNumber = ref('');
export const isExpandDriverSearch = ref(false);
export const isExpandVehicleSearch = ref(false);
export const emergencyReason = ref('');
export const approvedNameForEmergencyRide = ref('');
export const preDriverAcceptedTextMessage = ref('');
export const postDriverAcceptedTextMessage = ref('');
export const riderNote = ref('');
export const notesForLP = ref('');
export const isNeededToMonitorRide = ref(false);

export const deliveryInfoResult = ref<DeliveryInfoFormResult>({
  isValid: true,
  payload: null,
  notesForLP: '',
});
export type DeliveryInfoFormApi = {
  reset: () => void;
  validate: () => { isValid: boolean; errors: string[] };
};

export const deliveryInfoFormRef = ref<DeliveryInfoFormApi | null>(null);

export const setDeliveryInfoFormRef = (instance: Element | ComponentPublicInstance | null) => {
  deliveryInfoFormRef.value = instance as unknown as DeliveryInfoFormApi | null;
};


// Computed
export const hasCustomer = computed(() => !!customerInfo.value?.id);

export const isNewPreferredContactNumberVisible = computed(() => !orderRideRequest.value.riderPhone);

export const driverArrivals = computed(() => {
  const name = rideOrderData.value?.customRiderName || customerInfo.value?.firstName || 'rider';
  return [
    { label: 'Call Me', key: `Call ${name} upon arrival` },
    { label: 'Text Me', key: `Text ${name} upon arrival` },
    { label: 'Honk 3x', key: 'Honk 3x upon arrival' },
  ];
});

export const isShowDoNotApplyPartnerSubsidyField = computed(() => {
  if (!customerInfo.value?.partner?.id) {
    return false;
  }
  return true;
});

export const isDisableDoNotApplyPartnerSubsidyField = computed(() => {
  const isTransportation = orderRideRequest.value.serviceType === 'transportation';
  const isPharmacy = orderRideRequest.value.serviceType === 'pharmacyDelivery';
  const isSpecialRequest = orderRideRequest.value.serviceType === 'specialRequest';

  if (isTransportation) {
    return false;
  }
  if (isPharmacy && customerInfo.value?.partner?.isPharmacy) {
    return false;
  }
  if (isSpecialRequest && customerInfo.value?.partner?.isSpecialRequest) {
    return false;
  }
  return true;
});

export const isScheduleRide = computed(() => rideFormType.value === 'schedule');
export const serviceTypeOptions = computed(() => {
  // Nash cannot fulfil scheduled orders, so the test group can only schedule transportation.
  if (isScheduleRide.value && canOfferNashDelivery.value) {
    return availableServiceTypeList.value.filter((option) => option.key === RideServiceType.Transportation);
  }

  return availableServiceTypeList.value;
});
const preferredVendorRideTypes = computed(() => customerPartner.value?.preferredVendorRideTypes || []);
export const rideTypeOptions = computed(() =>
  isScheduleRide.value
    ? getScheduledRideTypes(customerPartnerCode.value || partnerCode.value, preferredVendorRideTypes.value)
    : getOrderRideTypes(),
);
export const isVendorScheduledRide = computed(() => {
  return isScheduleRide.value && orderRideRequest.value.scheduleKind !== 'multi' && orderRideRequest.value.serviceType === 'transportation';
});
export const shouldDisplaySurge = computed(() => {
  if (!isScheduleRide.value) {
    return false;
  }

  if (isVendorScheduledRide.value) {
    return false;
  }

  return customerPartner.value?.shouldDisplaySurge ?? true;
});

export const partnerEstimateTabs = computed(() => [
  { key: 'no-partner', label: 'No partner' },
  ...allPartnerEstimates.value.map((partnerEstimate, index) => ({
    key: `partner-${partnerEstimate.partnerId ?? index}`,
    label: `Partner #${index + 1}`,
  })),
]);
export const selectedPartnerEstimate = computed(() => {
  if (selectedPartnerEstimateIndex.value <= 0) {
    return null;
  }
  return allPartnerEstimates.value[selectedPartnerEstimateIndex.value - 1] || null;
});
export const selectedPartnerId = computed(() => selectedPartnerEstimate.value?.partnerId ?? null);

export const resetPartnerEstimates = () => {
  defaultEstimateData.value = null;
  allPartnerEstimates.value = [];
  selectedPartnerEstimateIndex.value = 0;
  isEstimationOptionsMode.value = false;
};

export const setPartnerEstimatesFromResponse = (data: any) => {
  const partnerEstimates = data.allPartnerEstimates;
  defaultEstimateData.value = data;
  allPartnerEstimates.value = Array.isArray(partnerEstimates) ? partnerEstimates : [];

  if (allPartnerEstimates.value.length > 0) {
    selectedPartnerEstimateIndex.value = 1;
    return allPartnerEstimates.value[0];
  }

  selectedPartnerEstimateIndex.value = 0;
  return data;
};

const applySingleEstimationData = (estimationData: any) => {
  estimationList.value = [];
  estimationRide.value = rideUtility.parseEstimationRide({
    estimationData,
  });
};

const applyMultiEstimationData = (estimationData: any) => {
  isInLyftAssistedTestGroup.value = Boolean(estimationData.isInLyftAssistedTestGroup);

  estimationList.value = rideUtility.parseEstimationRideMulti({
    apiResponse: estimationData,
    isRoundTrip: false,
  });

  if (estimationList.value.length === 1) {
    estimationRide.value = estimationList.value[0];
    return;
  }

  const defaultOptionRide = estimationList.value.find((ride) => ride.isDefaultOption);
  const rideMatchingTheTypeOfCar = estimationList.value.find((ride) => ride.rideType === orderRideRequest.value.rideType);
  estimationRide.value = defaultOptionRide ?? rideMatchingTheTypeOfCar ?? null;
};

export const applyPartnerEstimationData = async (estimationData: any) => {
  if (isEstimationOptionsMode.value) {
    applyMultiEstimationData(estimationData);
  } else {
    applySingleEstimationData(estimationData);
  }

  if (estimationRide.value && (estimationRide.value.surgeFactor > 1 || !!estimationRide.value.pricingExplanation)) {
    status.isUberSurge = true;
  } else {
    status.isUberSurge = false;
  }

  if (estimationRide.value?.warningMessage) {
    showWarningPopupDialog({
      text: estimationRide.value.warningMessage,
    });
  }
};

export const selectPartnerEstimateTab = async (index: number) => {
  if (index === selectedPartnerEstimateIndex.value) {
    return;
  }

  if (index === 0) {
    if (!defaultEstimateData.value) {
      return;
    }
    selectedPartnerEstimateIndex.value = 0;
    operatorConfirmedSurgePriceWithCustomer.value = false;
    await applyPartnerEstimationData(defaultEstimateData.value);
    return;
  }

  const partnerEstimate = allPartnerEstimates.value[index - 1];
  if (!partnerEstimate) {
    return;
  }

  selectedPartnerEstimateIndex.value = index;
  operatorConfirmedSurgePriceWithCustomer.value = false;
  await applyPartnerEstimationData(partnerEstimate);
};

// Re-export from composables
export {
  customerInfo,
  customerPartnerId,
  contactPhoneNumbers,
  isAllowedToExpandVehicleSearch,
  teamLeadsAndSupsList,
  isScheduledRideRestricted,
};

export const getRideOrderDataFromEstimateLink = (link: string): RideOrderData | null => {
  if (!link) return null;

  try {
    const queryStr = link.includes('?') ? link.split('?')[1] : link;
    const params = queryString.parse(queryStr) as Record<string, string | undefined>;

    const toNumber = (val: string | undefined): number | undefined => (val ? Number(val) : undefined);
    const toBoolean = (val: string | undefined): boolean => val === 'true';

    return {
      rideCostId: params.rideCostId || '',
      rideType: params.rideType || '',
      callerId: params.callerId,
      altCallerId: params.altCallerId,
      phoneNumber: params.phoneNumber,
      riderPhone: params.riderPhone,
      seatCount: toNumber(params.seatCount),
      startLat: toNumber(params.startLat),
      startLong: toNumber(params.startLong),
      startingAddress: params.startingAddress,
      startLocationId: params.startLocationId,
      startZoneId: params.startZoneId,
      startVenueId: params.startVenueId,
      startName: params.startName,
      destinationLat: toNumber(params.destinationLat),
      destinationLng: toNumber(params.destinationLng),
      destination: params.destination,
      destinationName: params.destinationName,
      partnerId: params.partnerId,
      noteToDriver: params.noteToDriver,
      isEmergency: toNumber(params.isEmergency),
      emergencyReason: params.emergencyReason,
      overrideBigCar: toBoolean(params.overrideBigCar),
      overrideSmallCar: toBoolean(params.overrideSmallCar),
      overrideBannedCar: toBoolean(params.overrideBannedCar),
      overrideBadDriver: toBoolean(params.overrideBadDriver),
      operatorNote: params.operatorNote,
      reason: params.reason,
      customRiderName: params.customRiderName,
      serviceType: params.serviceType,
      minutesToAutoRetryRequest: toNumber(params.minutesToAutoRetryRequest),
      minutesToAutoRetryRequestType: params.minutesToAutoRetryRequestType,
      isDoNotApplySubsidy: toBoolean(params.isDoNotApplySubsidy),
      isDoNotApplyExpiringCredits: toBoolean(params.isDoNotApplyExpiringCredits),
      isChargeRideOperatingFee: toBoolean(params.isChargeRideOperatingFee),
      isMedicalTrip: toBoolean(params.isMedicalTrip),
      partnerRidePurpose: params.partnerRidePurpose,
      storedLongRideOrderDataKey: params.storedLongRideOrderDataKey,
    };
  } catch {
    return null;
  }
};

export const buildCallRideUrlFromRideOrderData = (params: RideOrderData): string => {
  return `callRideFromEstimate?${queryString.stringify(
    {
      ...params,
      riderPhone: orderRideRequest.value.riderPhone || newPreferredContactNumber.value,
      isDoNotApplySubsidy: orderRideRequest.value.isDoNotApplySubsidy,
      isDoNotApplyExpiringCredits: orderRideRequest.value.isDoNotApplyExpiringCredits,
      minutesToAutoRetryRequestType: orderRideRequest.value.minutesToAutoRetryRequestType,
      minutesToAutoRetryRequest: orderRideRequest.value.minutesToAutoRetryRequest,
      notesForLP: isNeededToMonitorRide.value ? notesForLP.value || '' : '',
    },
    { skipNull: true, skipEmptyString: true },
  )}`;
};

export const updateDriverNotes = () => {
  if (!rideOrderData.value) return;

  driverNotePickUp.value = [
    pickupVenueNote.value && `🚪 ${pickupVenueNote.value}`,
    riderClothes.value && `👚 Look for: ${riderClothes.value}`,
    driverArrival.value && `☎️ ${driverArrival.value}`,
  ]
    .filter(Boolean)
    .join('\n');
};

export const generateDriverMessagePreview = async () => {
  if (!customerInfo.value?.id || !pickupLocation.value) {
    preDriverAcceptedTextMessage.value = '';
    postDriverAcceptedTextMessage.value = '';
    return;
  }

  try {
    const params = {
      riderNote: riderNote.value,
      callerId: String(customerInfo.value.id),
      serviceType: orderRideRequest.value.serviceType || 'transportation',
      customRiderName: rideOrderData.value?.customRiderName || customerInfo.value?.firstName || '',
      isEmergency: false,
      driverNotePickUp: driverNotePickUp.value,
      driverNoteDropOff: dropoffVenueNote.value,
      rideType: estimationRide.value?.rideType,
      pickupLocationName: pickupLocation.value.name || '',
      pickupLocationAddress: formatLocationAddress(pickupLocation.value) || '',
      startLat: pickupLocation.value.latitude ?? undefined,
      startLong: pickupLocation.value.longitude ?? undefined,
    };

    const response = await smsTemplateService.generateDriverAcceptedSmsExample(params);
    const [pre, post] = response.split('${riderNote}');
    preDriverAcceptedTextMessage.value = pre.trim().replaceAll('\n', '<br>');
    postDriverAcceptedTextMessage.value = post?.trim()?.replaceAll('\n', '<br>') ?? '';
  } catch (error) {
    console.error('generateDriverMessagePreview error:', error);
    preDriverAcceptedTextMessage.value = '';
  }
};

const debouncedGeneratePreview = debounce(() => {
  generateDriverMessagePreview();
}, 500);

export const showAccessibilityDialog = () => {
  status.isShowAccessibilityDialog = true;
};

export const hideAccessibilityDialog = () => {
  status.isShowAccessibilityDialog = false;
};

export const saveAccessibilityOptions = async (data: CallerAdditionalInfo) => {
  status.isShowAccessibilityDialog = false;

  if (!customerInfo.value?.id) return;

  status.loadingMessage = 'Saving Accessibility Options...';
  status.isLoading = true;

  try {
    await callerService.updateCaller({
      callerId: customerInfo.value.id,
      data,
    });

    setCustomerInfo({
      ...customerInfo.value,
      ...data,
    });

    updateDriverNotes();
  } catch (error) {
    console.error('Failed to save accessibility options:', error);
  } finally {
    status.isLoading = false;
  }
};

export const initDefaults = () => {
  if (!orderRideRequest.value.serviceType && availableServiceTypeList.value[0]?.key) {
    orderRideRequest.value.serviceType = availableServiceTypeList.value[0].key;
  }

  if (!orderRideRequest.value.rideType && rideTypeOptions.value[0]?.key) {
    orderRideRequest.value.rideType = rideTypeOptions.value[0].key;
  }

  orderRideRequest.value.phoneNumber = customerInfo.value?.phoneNumber ?? null;
  orderRideRequest.value.riderPhone = customerInfo.value?.phoneNumber ?? null;
};

export const initDriverMessagePreview = () => {
  if (!driverArrival.value && driverArrivals.value[0]) {
    driverArrival.value = driverArrivals.value[0].key;
  }
  updateDriverNotes();
  generateDriverMessagePreview();
};

export const initRideData = () => {
  rideOrderData.value = null;
  orderRideRequest.value.rideName = '';
  orderRideRequest.value.minutesBeforeCustomReminder = undefined;
  orderRideRequest.value.minutesAfterCreationCustomReminder = undefined;
  orderRideRequest.value.callToConfirmSurge = null;
  orderRideRequest.value.scheduleKind = 'single';
  orderRideRequest.value.pickupDate = null;
  orderRideRequest.value.pickupTime = null;
  orderRideRequest.value.repeatEveryXWeek = 1;
  orderRideRequest.value.day0 = false;
  orderRideRequest.value.day1 = false;
  orderRideRequest.value.day2 = false;
  orderRideRequest.value.day3 = false;
  orderRideRequest.value.day4 = false;
  orderRideRequest.value.day5 = false;
  orderRideRequest.value.day6 = false;
  orderRideRequest.value.endOnType = 'never';
  orderRideRequest.value.endAfterXTimes = 1;
  orderRideRequest.value.endOn = null;
  orderRideRequest.value.scheduledAppointmentDate = null;
  orderRideRequest.value.scheduledAppointmentTime = null;
  orderRideRequest.value.scheduledAppointmentBufferMinutes = 30;
  orderRideRequest.value.operatorNote = '';
  orderRideRequest.value.reason = '';
  orderRideRequest.value.isDoNotApplySubsidy = false;
  orderRideRequest.value.isDoNotApplyExpiringCredits = false;
  newPreferredContactNumber.value = '';
  isExpandDriverSearch.value = customerInfo.value?.isExpandDriverSearch || false;
  isExpandVehicleSearch.value = customerInfo.value?.isExpandVehicleSearch || false;
  riderClothes.value = '';
  riderNote.value = '';
  driverArrival.value = '';
  notesForLP.value = '';
  isNeededToMonitorRide.value = false;
  deliveryInfoFormRef.value?.reset();
};

export const isDelivery = computed(() => isDeliveryServiceType(orderRideRequest.value.serviceType));
// Only operators in the Nash AB test group get the new delivery forms; everyone else keeps the question flow.
export const isNashDelivery = computed(() => isDelivery.value && canOfferNashDelivery.value);

watch(
  () => orderRideRequest.value.serviceType,
  (serviceType) => {
    orderRideRequest.value.operatorNote = '';
    orderRideRequest.value.reason = '';

    if (serviceType === RideServiceType.PharmacyDelivery) {
      orderRideRequest.value.reason = 'Pharmacy Delivery';
    }
  },
);

watch(rideFormType, (formType) => {
  if (formType !== 'schedule') {
    return;
  }

  if (isNashDelivery.value) {
    rideFormType.value = 'now';
    return;
  }

  deliveryInfoFormRef.value?.reset();
});

// Watches
watch(
  rideOrderData,
  (data) => {
    if (data) {
      if (!driverArrival.value) {
        driverArrival.value = driverArrivals.value[0]?.key || '';
      }
      updateDriverNotes();
      generateDriverMessagePreview();
    }
  },
  { immediate: true },
);

watch(step, (newStep) => {
  if (newStep === RideOrderingStep.Estimate && rideOrderData.value) {
    if (!driverArrival.value) {
      driverArrival.value = driverArrivals.value[0]?.key || '';
    }
    updateDriverNotes();
    generateDriverMessagePreview();
  }
});

watch([driverArrival, pickupVenueNote, riderClothes, riderNote], () => {
  updateDriverNotes();
  debouncedGeneratePreview();
});

watch(isExpandDriverSearch, (value: boolean) => {
  if (rideOrderData.value) {
    rideOrderData.value.overrideBadDriver = value;
  }
});

watch(isExpandVehicleSearch, (value: boolean) => {
  if (rideOrderData.value) {
    rideOrderData.value.overrideBigCar = value;
    rideOrderData.value.overrideSmallCar = value;
    rideOrderData.value.overrideBannedCar = value;
  }
});

watch([emergencyReason, approvedNameForEmergencyRide], ([reason, approvedBy]) => {
  if (rideOrderData.value) {
    if (reason && approvedBy) {
      rideOrderData.value.emergencyReason = `${reason}\n\nEmergency ride was approved by ${approvedBy}`;
    } else {
      rideOrderData.value.emergencyReason = reason || undefined;
    }
  }
});

// Initialize expand search values from customer info
watch(
  customerInfo,
  (value, oldValue) => {
    if (value && oldValue && value.id && value.id === oldValue.id) {
      return;
    }

    step.value = RideOrderingStep.Location;
    rideOrderData.value = null;
    status.errorMessage = '';
    status.isUberSurge = false;
    operatorConfirmedSurgePriceWithCustomer.value = false;

    resetRideLocations();
    initRideData();
    initDefaults();

    if (value?.id) {
      void loadRecentRideLocations(String(value.id));
    }
  },
  { immediate: true },
);

watch(estimationRide, (value: RideEstimate | null) => {
  debouncedGeneratePreview();

  if (!value || !value.callRideFromEstimateLink) {
    rideOrderData.value = null;
    return;
  }

  rideOrderData.value = getRideOrderDataFromEstimateLink(value.callRideFromEstimateLink);
});

watch(
  rideTypeOptions,
  () => {
    orderRideRequest.value.rideType = getAvailableRideType({
      rideTypes: rideTypeOptions.value,
      partnerCode: customerPartnerCode.value,
      customerInfo: customerInfo.value,
    });
  },
  {
    immediate: true,
  },
);
